import type { TripCashFigure, TripCashReconciliationView } from '@chukua/common';
import { Alert } from 'antd';
import { Coins } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '../../../shared/components/EmptyState';
import { RowListSkeleton } from '../../../shared/components/RowListSkeleton';
import { useTripCash } from '../hooks/use-trip-cash';
import { TripCashFigureRow } from './TripCashFigureRow';
import { TripCashSettleDrawer, type SettleMode } from './TripCashSettleDrawer';

interface TripCashPanelProps {
  tripId: string;
  tripNumber: string;
  canRecord: boolean;
}

function PendingNotice({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <Alert
      className="mb-3"
      type="warning"
      showIcon
      title={`${count} expense${count === 1 ? ' is' : 's are'} still waiting for a decision. Cash can be settled once they are decided.`}
    />
  );
}

function Figures({
  reconciliation,
  canRecord,
  onSettle,
}: {
  reconciliation: TripCashReconciliationView;
  canRecord: boolean;
  onSettle: (f: TripCashFigure, m: SettleMode) => void;
}) {
  if (reconciliation.figures.length === 0) {
    return (
      <EmptyState
        icon={Coins}
        title="No cash was given for this trip"
        description="Advances and top-ups show here against the expenses approved for the trip."
      />
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <PendingNotice count={reconciliation.expenses.pending} />
      {reconciliation.figures.map((figure) => (
        <TripCashFigureRow
          key={`${figure.driverId}-${figure.currency}`}
          figure={figure}
          pendingExpenses={reconciliation.expenses.pending}
          canRecord={canRecord}
          onSettle={(mode) => onSettle(figure, mode)}
        />
      ))}
    </div>
  );
}

/** What the driver was given for the trip against the expenses approved, and how the rest was left (gitaumoses4/chukua#117). */
export function TripCashPanel({ tripId, tripNumber, canRecord }: TripCashPanelProps) {
  const { state, settle, isSubmitting, error, clearError } = useTripCash(tripId, tripNumber);
  const [open, setOpen] = useState<{ figure: TripCashFigure; mode: SettleMode } | null>(null);

  async function submit(input: Parameters<typeof settle>[0]): Promise<boolean> {
    const done = await settle(input);
    if (done) setOpen(null);
    return done;
  }
  function close() {
    clearError();
    setOpen(null);
  }

  return (
    <section aria-label="Cash reconciliation" className="rounded-lg border border-line bg-surface p-5">
      <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-fg">
        <Coins className="size-5 text-muted" aria-hidden /> Cash reconciliation
      </h2>
      {state.status === 'loading' && <RowListSkeleton label="Loading cash reconciliation" rows={1} />}
      {state.status === 'error' && <p className="text-sm text-muted">Couldn&apos;t load the cash position. Try reloading the page.</p>}
      {state.status === 'loaded' && (
        <Figures reconciliation={state.reconciliation} canRecord={canRecord} onSettle={(figure, mode) => setOpen({ figure, mode })} />
      )}
      {open && <TripCashSettleDrawer figure={open.figure} mode={open.mode} isSubmitting={isSubmitting} error={error} onSubmit={submit} onClose={close} />}
    </section>
  );
}
